import React from 'react';
import moment from 'moment';
import {
  AmPmType,
  BrandOption,
  BrandType,
  BrandWorkingHoursType,
  MealAddonsType,
  MealCategoryType,
  MealType,
  SearchType,
  TimeSelectorType,
  WorkingHoursType
} from './types';
import { ENUM_BRAND_TYPE, ENUM_CATEGORY, ENUM_SEARCH_TYPE } from './enum';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { STORAGE_KEYS } from './constants';
import { apiService } from 'services/base.service';

export const numberWithCommas = (x: string | number): string => {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// eslint-disable-next-line

const MONTHS_OF_YEAR = [
  'Janaury',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const isDateValid = (date: string): boolean => {
  const dateValue = new Date(date);
  return !isNaN(dateValue.getTime());
};

const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getDate = (date: string): number | null | string => {
  const dateValue = new Date(date);
  const value = dateValue.getDate();
  const ordinalValue = getOrdinal(value);
  return isDateValid(date) ? ordinalValue : null;
};

export const getMonth = (date: string): string | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? MONTHS_OF_YEAR[dateValue.getMonth() ?? 0] : null;
};

export const getShortMonth = (date: string): string | null => {
  const month = getMonth(date);
  return month ? month.slice(0, 3) : null;
};

export const getYear = (date: string): number | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? dateValue.getFullYear() : null;
};

export const getSimpleDateFormat = (date: string): string | null => {
  return `${getDate(date)} ${getShortMonth(date)} ${getYear(date)}`;
};

export const formatDate = (timeStr: string) => {
  const dateString = new Date(timeStr).toDateString();
  // remove day
  return dateString.split(' ').slice(1).join(' ');
};

export const formatPhoneNumber = (number: string) => {
  // add - after every three characters
  let formattedNumber = '';
  for (let index = 0; index < number.length - 1; index += 1) {
    formattedNumber += number[index];
    if ((index + 1) % 3 === 0) {
      formattedNumber += '-';
    }
  }
  return formattedNumber;
};

export const formatSurgeDate = (date: any) => {
  const dateSet = String(date);
  const dateArray = dateSet?.split('-');
  const startDate = new Date(dateArray?.slice(0, 3).join('-'));
  const endDate = dateArray.length > 3 ? new Date(dateArray?.slice(3).join('-')) : startDate;

  return { startDate, endDate };
};
export const dateDifference = (a: any, b: any) => {
  const c = moment(b);
  const d = moment(a);

  return c.diff(d, 'days'); // 86400000
};

export const getDoubleDigitsNumber = (number: number) => {
  if (number < 10) return `0${number}`;
  return `${number}`;
};
export const formatTime = (timeObj: Date) => {
  return `${getDoubleDigitsNumber(timeObj.getHours())} : ${getDoubleDigitsNumber(
    timeObj.getUTCMinutes()
  )}`;
};

export const getDurationSince = (timeObj: Date, timeSince: Date) => {
  const timeDivisions = [
    { name: 'days', duration: 24 * 60 * 60 * 1000 },
    { name: 'hours', duration: 60 * 60 * 1000 },
    { name: 'mins', duration: 60 * 1000 },
    { name: 'sec', duration: 1000 }
  ];
  const diff = timeSince.getTime() - timeObj.getTime();
  const division = timeDivisions.find((division) => {
    if (diff / division.duration > 1) return true;
    return false;
  });
  if (!division) return '0 mins';
  return `${Math.ceil(diff / division.duration)} ${division.name}`;
};
export const convertTimeto12hrs = (time: any) => {
  // Check correct time format and split into components
  time = time?.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

  if (time?.length > 1) {
    // If time format correct
    time = time.slice(1); // Remove full string match value
    time[5] = +time[0] <= 12 ? 'AM' : 'PM'; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
    time.splice(3, 1);
  }
  return time.join(''); // return adjusted time or original string
};

export const getGroupedWorkingHours = (workingHours: BrandWorkingHoursType[]) => {
  const groupedWorkingHours: { [key: string]: BrandWorkingHoursType[] } = {};

  for (const workingHour of workingHours) {
    const key = `${workingHour.opening_time}-${workingHour.closing_time}`;
    if (!groupedWorkingHours[key]) {
      groupedWorkingHours[key] = [];
    }
    groupedWorkingHours[key].push(workingHour);
  }
  const groupedWorkingHoursArray = Object.values(groupedWorkingHours);
  const resultArray: { day: string; time: string }[] = [];
  for (const group of groupedWorkingHoursArray) {
    const { opening_time, closing_time } = group[0];
    const newOpeningTime = moment(opening_time, 'HH:mm').format('hh:mm');
    const newClosingTime = moment(closing_time, 'HH:mm').format('hh:mm');
    const time = `${newOpeningTime}am - ${newClosingTime}pm`;
    const names = group.map((obj) => (obj.active ? obj.name : null));
    const shortenedNames = names.map((name) => {
      const shortenedName = name?.substring(0, 3).toLowerCase();
      if (shortenedName) return shortenedName?.charAt(0).toUpperCase() + shortenedName?.slice(1);
    });
    resultArray.push({ day: shortenedNames?.join(', '), time });
  }
  return resultArray;
};

export const getFileSize = (size: number) => {
  let fileSize;

  if (size < 1024) {
    fileSize = size + ' bytes';
  } else if (size < 1024 * 1024) {
    fileSize = (size / 1024).toFixed(2) + ' kb';
  } else if (size < 1024 * 1024 * 1024) {
    fileSize = (size / (1024 * 1024)).toFixed(2) + ' mb';
  }

  return fileSize;
};

export const getTimeDuration = (durationInSeconds: number) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = Math.floor(durationInSeconds % 60);
  let timeString;
  if (hours) {
    timeString = `${hours} hrs ${minutes} mins ${seconds} s`;
  } else if (minutes) {
    timeString = `${minutes} mins ${seconds} s`;
  } else {
    timeString = `${seconds} s`;
  }
  return timeString;
};

export function convertTo12HourFormat(time: string) {
  const [hours, minutes, seconds] = time.split(':');
  let convertedHours = Number(hours) % 12;
  convertedHours = convertedHours === 0 ? 12 : convertedHours;
  const formattedTime = `${convertedHours}:${minutes}:${seconds}`;

  return formattedTime;
}

export const getApiFormatWorkingHours = (workingHours: any) => {
  const formattedArray: any = [];
  workingHours?.map((item: any) =>
    formattedArray.push({
      name: item.name,
      opening_time: `${item?.opening_time?.hours}:${item.opening_time?.minutes} ${item.opening_time.amPm}`,
      closing_time: `${item?.closing_time?.hours}:${item.closing_time?.minutes} ${item.closing_time.amPm}`,
      active: item.active
    })
  );
  return formattedArray;
};
export const getWorkingHoursComponentFormatFromApi = (workingHours: WorkingHoursType[]) => {
  const formattedArray: any = [];
  workingHours?.map((item: any) =>
    formattedArray.push({
      name: item.name,
      opening_time: {
        amPm: moment(item.opening_time).format('a').toUpperCase(),
        hours: moment(item.opening_time).format('hh'),
        minutes: moment(item.opening_time).format('mm'),
        timeWithColon: moment(item.opening_time).format('hh:mm'),
        totalHours: '12'
      },
      closing_time: {
        amPm: moment(item.closing_time).format('a').toUpperCase(),
        hours: moment(item.closing_time).format('hh'),
        minutes: moment(item.closing_time).format('mm'),
        timeWithColon: moment(item.closing_time).format('hh:mm'),
        totalHours: '12'
      },
      active: item.active
    })
  );
  return formattedArray;
};

export const formatTimeToApiFormat = (timeObj: any) => {
  let hours = Number(timeObj.hours);
  const minutes = timeObj.minutes.padStart(2, '0');
  const seconds = '00';

  // Convert to 24-hour format
  if (timeObj.amPm === 'PM' && hours !== 12) {
    hours += 12;
  } else if (timeObj.amPm === 'AM' && hours === 12) {
    hours = 0;
  }

  hours = Number(hours.toString().padStart(2, '0'));

  return `${hours}:${minutes}:${seconds}`;
};

export const newMealFilter = (value: string, list: BrandOption[]) =>
  list.filter((item) => item.text.toLowerCase().includes(value.toLowerCase()));

export const isEditMeal = (pathname: string) => {
  return pathname.includes('edit-meal');
};

export const getSearchReponseData = (res: any, type: ENUM_SEARCH_TYPE): SearchType[] => {
  let data: SearchType[] = [];
  if (type === ENUM_SEARCH_TYPE.ADDON) {
    const resCopy = res as MealAddonsType[];
    resCopy?.map(
      (addon) =>
        (data = data.concat({
          id: addon.id,
          images: addon?.meal_data?.images,
          name: addon?.meal_data?.name,
          amount: addon?.meal_data?.amount,
          brandLogo: addon?.meal_data?.brand?.logo,
          meal_addon_category: addon?.meal_addon_category,
          meal_addon_id: addon?.id,
          parent_meal_data: addon?.parent_meal_data,
          updated_at: addon?.updated_at
        }))
    );
  } else if (type === ENUM_SEARCH_TYPE.MEAL) {
    const resCopy = res as MealType[];
    resCopy?.map(
      (meal) =>
        (data = data.concat({
          id: meal.id,
          images: meal?.images,
          name: meal?.name,
          amount: meal?.amount,
          brandLogo: meal?.images,
          brand_id: meal?.brand_id,
          updated_at: meal?.updated_at
        }))
    );
  } else if (type === ENUM_SEARCH_TYPE.BRAND) {
    const resCopy = res as BrandType[];
    resCopy?.map(
      (brand) =>
        (data = data.concat({
          archived: brand?.archived,
          id: brand.id,
          images: brand?.images,
          name: brand?.name,
          logo: brand?.logo,
          description: brand?.description,
          updated_at: brand?.updated_at,
          is_private: brand?.is_private,
          brand_type: brand?.brand_type
        }))
    );
  } else if (type === ENUM_SEARCH_TYPE.MEAL_CATEGORY || type === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
    const resCopy = res as MealCategoryType[];
    resCopy?.map(
      (cat) =>
        (data = data.concat({
          id: cat.id,
          name: cat?.name,
          created_at: cat?.created_at,
          summary: cat?.summary,
          brand_id: cat?.brand_id,
          meal_parent_id: cat?.meal_parent_id,
          updated_at: cat?.updated_at
        }))
    );
  }

  return data;
};

export const getFilterName = (pathname: string) => {
  const searchFilter = pathname.split('/')[4];

  let name = '';
  if (searchFilter === ENUM_SEARCH_TYPE.MEAL) {
    name = 'meals';
  } else if (searchFilter === ENUM_SEARCH_TYPE.MEAL_CATEGORY) {
    name = 'meal category';
  } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
    name = 'addon category';
  }
  return name;
};

export const getTrimmedPath = () => {
  const path = location.pathname;
  // eslint-disable-next-line
  const [empty, ...otherPath] = path.split('/');
  return otherPath.join('/');
};

export function mergeArrays(array1: any[], array2: any[], key = 'id') {
  const [smallerArr, biggerArr] =
    array1.length > array2.length ? [array2, array1] : [array1, array2];
  const smallerArrIds = smallerArr.map((a) => a[key]);
  const filtered = biggerArr.reduce((acc, item) => {
    if (!smallerArrIds.includes(item[key])) acc.push(item);
    return acc;
  }, []);

  return [...filtered, ...smallerArr];
}

export const cleanObject = (obj: any) => {
  const newObj: any = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== '' && value !== null && value !== undefined && value?.length !== 0) {
      newObj[key] = value;
    }
  });
  return newObj;
};
export const checkImageUrlValidity = (urls: string[]) => {
  if (urls.length === 0) return urls;
  const pattern = /^(?:https?|ftp):\/\/(?:www\.)?[a-zA-Z0-9\-.]+(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?$/;
  return urls.filter((url) => pattern.test(url));
};

export const getDefaultImages = (MAX_UPLOAD: number, images: string[]) => {
  const imagesUploaded = images?.length ? [...images] : [];
  const imagesUploadedCount = imagesUploaded.length;
  const imagesNotUploadedCount = MAX_UPLOAD - imagesUploadedCount;
  const imagesNotUploaded = Array(imagesNotUploadedCount).fill('');
  return imagesUploaded.concat(imagesNotUploaded);
};

export const isFCBCustomBrand = (type: ENUM_BRAND_TYPE) => {
  return [
    ENUM_BRAND_TYPE.FCB_CUSTOM_BREAKFAST,
    ENUM_BRAND_TYPE.FCB_CUSTOM_LUNCH,
    ENUM_BRAND_TYPE.FCB_CUSTOM_AFTERHOURS
  ].includes(type);
};

export const isFCBBrand = (type: ENUM_BRAND_TYPE) => {
  return isFCBCustomBrand(type) || type === ENUM_BRAND_TYPE.FOODCOURT_BUSINESS;
};

export const createTimeSelector = (time: string): TimeSelectorType => {
  const formattedTime = moment(time, 'HH:mm').format('hh:mmA');
  const amPm = formattedTime.slice(-2) as AmPmType;
  const [hours, minutes] = formattedTime.replace(amPm, '').split(':');
  let totalHours = hours;
  if (amPm === 'PM') {
    if (+hours === 12) totalHours = '00';
    else totalHours = `${+hours + 12}`;
  }
  const timeWithColon = `${totalHours}:${minutes}`;
  return {
    hours: hours.padStart(2, '0'),
    minutes: minutes.padStart(2, '0'),
    amPm,
    timeWithColon,
    totalHours
  };
};

export const formatTimeSelector = (time: TimeSelectorType) => {
  return `${time.hours}:${time.minutes} ${time.amPm}`;
};

export const handleApiError = (error: unknown, fallback = 'An error occurred') => {
  const message = (error as any)?.response?.data?.message;
  const messages = Array.isArray(message) ? message : [message];
  messages.forEach((message) => {
    toast(<ToastMessage type='error' message={message || fallback} />);
  });
};

export const isChowdeckOrder = (brand_type: ENUM_BRAND_TYPE): boolean => {
  return brand_type === ENUM_BRAND_TYPE.CHOWDECK;
};
export const isGlovoOrder = (brand_type: ENUM_BRAND_TYPE): boolean => {
  return brand_type === ENUM_BRAND_TYPE.GLOVO;
};
export const isManoOrder = (brand_type: ENUM_BRAND_TYPE): boolean => {
  return brand_type === ENUM_BRAND_TYPE.MANO;
};

export const isFCShopCategory = () =>
  localStorage.getItem(STORAGE_KEYS.CATEGORY) === ENUM_CATEGORY.SHOP;

export const getComplimentaryMeals = async (setComplimentaryMeals: (arg0: MealType[]) => void) => {
  const brandId = process.env.REACT_APP_COMPLIMENTARY_BRAND_ID;
  try {
    const res = await apiService.get<{ data: MealType[] }>(`meals/${brandId}`, {
      isInternal: true
    });
    setComplimentaryMeals(res.data.data);
  } catch (error) {
    handleApiError(error);
  }
};

export const getMealNameAndImages = (id: string, complimentaryMeals: MealType[]) => {
  const meal = complimentaryMeals.find((m) => m.id === id);
  return meal
    ? {
        name: meal.name,
        images: meal.images
      }
    : { name: '', images: [] };
};

export const getWordsCount = (body: string) => {
  // Parse the HTML string from the blog body
  let htmlString = '';
  try {
    htmlString = JSON.parse(body ?? '""');
  } catch {
    htmlString = '';
  }
  // Remove HTML tags to get plain text
  const plainText = typeof htmlString === 'string' ? htmlString.replace(/<[^>]+>/g, ' ') : '';
  // Count words
  return plainText.trim().split(/\s+/).filter(Boolean).length;
};

export const generateSlug = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};
